import { msg } from '@lingui/core/macro';

import { Permission } from '@/api';

export const PERMISSION_DESCRIPTIONS: Record<Permission, ReturnType<typeof msg>> = {
  accept_dispatch: msg({
    id: 'permission.accept_dispatch.description',
    message: 'Review and accept or reject incoming repair jobs during the dispatch phase.',
  }),
  accept_requalification: msg({
    id: 'permission.accept_requalification.description',
    message: 'Review and approve new quotes when an article requires requalification.',
  }),
  act_on_behalf_of_non_digitalized_workshop: msg({
    id: 'permission.act_on_behalf_of_non_digitalized_workshop.description',
    message: 'Perform actions on behalf of workshops that are not using the platform.',
  }),
  analyze_article: msg({
    id: 'permission.analyze_article.description',
    message: 'Inspect articles to assess their condition and determine required services.',
  }),
  archive_article: msg({
    id: 'permission.archive_article.description',
    message: 'Archive articles that are no longer active or relevant.',
  }),
  choose_article_service: msg({
    id: 'permission.choose_article_service.description',
    message: 'Select the appropriate service type for an article (repair, cleaning, etc.).',
  }),
  confirm_exchange_stock_availability: msg({
    id: 'permission.confirm_exchange_stock_availability.description',
    message: 'Confirm the availability of stock for article exchanges.',
  }),
  control_quality: msg({
    id: 'permission.control_quality.description',
    message: 'Perform quality control checks on completed repairs before returning articles.',
  }),
  create_client_request: msg({
    id: 'permission.create_client_request.description',
    message: 'Create new repair or service requests on behalf of clients.',
  }),
  create_invoice: msg({
    id: 'permission.create_invoice.description',
    message: 'Generate invoices for completed services.',
  }),
  create_shipment_from_origin: msg({
    id: 'permission.create_shipment_from_origin.description',
    message: 'Create a shipment to send articles from their origin location.',
  }),
  create_store_request: msg({
    id: 'permission.create_store_request.description',
    message: 'Create new repair or service requests from a store.',
  }),
  edit_article: msg({
    id: 'permission.edit_article.description',
    message: 'Modify article details such as description, photos, or product information.',
  }),
  edit_client: msg({
    id: 'permission.edit_client.description',
    message: 'Update client contact information and details.',
  }),
  edit_customs_data: msg({
    id: 'permission.edit_customs_data.description',
    message: 'Edit customs-related data for international shipments.',
  }),
  exchange_article: msg({
    id: 'permission.exchange_article.description',
    message: 'Process article exchanges when a repair is not possible.',
  }),
  manage_billing: msg({
    id: 'permission.manage_billing.description',
    message: 'Access and manage billing settings, payment methods, and subscription plans.',
  }),
  manage_entities: msg({
    id: 'permission.manage_entities.description',
    message: 'Create, edit, and delete stores and workshops across the organization.',
  }),
  manage_entity: msg({
    id: 'permission.manage_entity.description',
    message: 'Edit settings and configuration for a specific store or workshop.',
  }),
  manage_organization: msg({
    id: 'permission.manage_organization.description',
    message: 'Access and modify organization-wide settings and configurations.',
  }),
  manage_users: msg({
    id: 'permission.manage_users.description',
    message: 'Invite, edit, and deactivate user accounts and manage role assignments.',
  }),
  moderate_comment: msg({
    id: 'permission.moderate_comment.description',
    message: 'Delete or hide inappropriate comments on requests.',
  }),
  repair_article: msg({
    id: 'permission.repair_article.description',
    message: 'Perform and log repair work on articles.',
  }),
  supervise_request: msg({
    id: 'permission.supervise_request.description',
    message: 'Oversee and manage the full lifecycle of repair requests.',
  }),
  validate_invoice: msg({
    id: 'permission.validate_invoice.description',
    message: 'Review and approve invoices before they are sent.',
  }),
  validate_payment: msg({
    id: 'permission.validate_payment.description',
    message: 'Confirm and validate incoming payments.',
  }),
  verify_shipment_reception: msg({
    id: 'permission.verify_shipment_reception.description',
    message: 'Confirm receipt of shipments and verify article condition on arrival.',
  }),
  view_all_collaborators: msg({
    id: 'permission.view_all_collaborators.description',
    message: 'See the list of all collaborators across the organization.',
  }),
  view_all_shipments_issues: msg({
    id: 'permission.view_all_shipments_issues.description',
    message: 'Access the list of all shipment issues and incidents.',
  }),
  view_analytics: msg({
    id: 'permission.view_analytics.description',
    message: 'Access dashboards and analytics reports.',
  }),
  view_client_communication: msg({
    id: 'permission.view_client_communication.description',
    message: 'Read messages and emails exchanged with clients.',
  }),
  view_invoice: msg({
    id: 'permission.view_invoice.description',
    message: 'View invoice details and history.',
  }),
  write_client_communication: msg({
    id: 'permission.write_client_communication.description',
    message: 'Send messages and emails to clients.',
  }),
};

export const PERMISSION_LABELS: Record<Permission, ReturnType<typeof msg>> = {
  accept_dispatch: msg({
    id: 'permission.accept_dispatch',
    message: 'Dispatch (job review)',
  }),
  accept_requalification: msg({
    id: 'permission.accept_requalification',
    message: 'New quote analysis',
  }),
  act_on_behalf_of_non_digitalized_workshop: msg({
    id: 'permission.act_on_behalf_of_non_digitalized_workshop',
    message: 'Act on behalf of non-digitalised workshop',
  }),
  analyze_article: msg({
    id: 'permission.analyze_article',
    message: 'Analyze article',
  }),
  archive_article: msg({
    id: 'permission.archive_article',
    message: 'Archive article',
  }),
  choose_article_service: msg({
    id: 'permission.choose_article_service',
    message: 'Choose article service',
  }),
  confirm_exchange_stock_availability: msg({
    id: 'permission.confirm_exchange_stock_availability',
    message: 'Confirm exchange stock availability',
  }),
  control_quality: msg({
    id: 'permission.control_quality',
    message: 'Control quality',
  }),
  create_client_request: msg({
    id: 'permission.create_client_request',
    message: 'Create client request',
  }),
  create_invoice: msg({
    id: 'permission.create_invoice',
    message: 'Create invoice',
  }),
  create_shipment_from_origin: msg({
    id: 'permission.create_shipment_from_origin',
    message: 'Create shipment from origin',
  }),
  create_store_request: msg({
    id: 'permission.create_store_request',
    message: 'Create store request',
  }),
  edit_article: msg({
    id: 'permission.edit_article',
    message: 'Edit article',
  }),
  edit_client: msg({
    id: 'permission.edit_client',
    message: 'Edit client',
  }),
  edit_customs_data: msg({
    id: 'permission.edit_customs_data',
    message: 'Edit customs data',
  }),
  exchange_article: msg({
    id: 'permission.exchange_article',
    message: 'Exchange article',
  }),
  manage_billing: msg({
    id: 'permission.manage_billing',
    message: 'Manage billing',
  }),
  manage_entities: msg({
    id: 'permission.manage_entities',
    message: 'Manage entities',
  }),
  manage_entity: msg({
    id: 'permission.manage_entity',
    message: 'Manage entity',
  }),
  manage_organization: msg({
    id: 'permission.manage_organization',
    message: 'Manage organization',
  }),
  manage_users: msg({
    id: 'permission.manage_users',
    message: 'Manage users',
  }),
  moderate_comment: msg({
    id: 'permission.moderate_comment',
    message: 'Moderate comments',
  }),
  repair_article: msg({
    id: 'permission.repair_article',
    message: 'Repair article',
  }),
  supervise_request: msg({
    id: 'permission.supervise_request',
    message: 'Supervise request',
  }),
  validate_invoice: msg({
    id: 'permission.validate_invoice',
    message: 'Validate invoice',
  }),
  validate_payment: msg({
    id: 'permission.validate_payment',
    message: 'Validate payment',
  }),
  verify_shipment_reception: msg({
    id: 'permission.verify_shipment_reception',
    message: 'Verify shipment reception',
  }),
  view_all_collaborators: msg({
    id: 'permission.view_all_collaborators',
    message: 'View all collaborators',
  }),
  view_all_shipments_issues: msg({
    id: 'permission.view_all_shipments_issues',
    message: 'View all shipment issues',
  }),
  view_analytics: msg({
    id: 'permission.view_analytics',
    message: 'View analytics',
  }),
  view_client_communication: msg({
    id: 'permission.view_client_communication',
    message: 'View client communication',
  }),
  view_invoice: msg({
    id: 'permission.view_invoice',
    message: 'View invoice',
  }),
  write_client_communication: msg({
    id: 'permission.write_client_communication',
    message: 'Write client communication',
  }),
};
